// client/src/components/pages/Settings/LexiStarDataExport/exportLogic.ts
//
// Pure helpers of the Data export page (ST-00207): the dataset groups of the checklist, the
// fallback catalogue (before the server answers), the default range, the positions limit and
// the display formats (SAST times, sizes, progress).

import { toApiDateString } from '../../../../utils/dateUtils';
import { DATASET_KEYS, type DatasetInfo, type DatasetKey, type ExportJob, type ExportLimits, type ExportState } from './exportApi';

export interface DatasetGroup {
  id: string;
  label: string;
  keys: DatasetKey[];
}

/** The checklist, grouped the way people think about the data. */
export const DATASET_GROUPS: readonly DatasetGroup[] = [
  { id: 'fleet', label: 'Fleet and drivers', keys: ['assets', 'drivers', 'driver_consents', 'driver_asset_allocations'] },
  { id: 'network', label: 'Routes and sites', keys: ['lanes', 'routes', 'geofences'] },
  { id: 'trips', label: 'Trips', keys: ['trips', 'trip_legs', 'trip_timeline', 'deviations', 'runs'] },
  { id: 'activity', label: 'Activity in the date range', keys: ['events', 'messages', 'cost_ledger', 'positions'] },
  { id: 'account', label: 'Account', keys: ['usage_statements', 'report_runs'] },
];

const LABELS: Record<DatasetKey, [string, string]> = {
  assets: ['Assets', 'Trucks, trailers and loaders.'],
  drivers: ['Drivers', 'Names, tags, phone numbers and Telegram chats.'],
  driver_consents: ['Driver consents', 'Opt-ins and opt-outs per channel.'],
  driver_asset_allocations: ['Driver allocations', 'Which driver drove which truck, and when.'],
  lanes: ['Lanes', 'Directional lanes between sites.'],
  routes: ['Routes', 'Approved routes and their versions (GeoJSON).'],
  geofences: ['Geofences', 'Site zones and their rules.'],
  trips: ['Trips', 'Every trip with its figures.'],
  trip_legs: ['Trip legs', 'Loaded and empty legs of each trip.'],
  trip_timeline: ['Trip timelines', 'What happened on each trip, in order.'],
  deviations: ['Deviations', 'Route deviations and the decisions.'],
  runs: ['Runs', 'Shifts per truck and day.'],
  events: ['Events', 'Everything LexiStar detected.'],
  messages: ['Messages', 'Messages to and from drivers and controllers.'],
  cost_ledger: ['Message costs', 'The cost of every message sent.'],
  positions: ['Positions', 'Every GPS position of your trucks.'],
  usage_statements: ['Usage statements', 'Monthly usage statements.'],
  report_runs: ['Report runs', 'Which reports were made (the list, not the files).'],
};

const DATED: readonly DatasetKey[] = ['events', 'messages', 'cost_ledger', 'positions'];
const PERSONAL: readonly DatasetKey[] = ['drivers', 'driver_consents', 'messages'];

/** The catalogue until the server's arrives (same keys, short texts). */
export const FALLBACK_DATASETS: readonly DatasetInfo[] = DATASET_KEYS.map((key) => ({
  key,
  label: LABELS[key][0],
  description: LABELS[key][1],
  dated: DATED.includes(key),
  personal: PERSONAL.includes(key),
  files: key === 'routes' ? ['routes.csv', 'route_versions.csv'] : [`${key}.csv`],
  available: true,
}));

export const labelOf = (key: string, catalogue: readonly DatasetInfo[] = FALLBACK_DATASETS): string =>
  catalogue.find((d) => d.key === key)?.label ?? LABELS[key as DatasetKey]?.[0] ?? key;

/** The default range: the last `days` days, today included (local calendar dates). */
export function defaultRange(days: number, now: Date = new Date()): { start: Date; end: Date } {
  const end = new Date(now.getFullYear(), now.getMonth(), now.getDate());
  const start = new Date(end);
  start.setDate(start.getDate() - (days - 1));
  return { start, end };
}

/** Inclusive calendar days between two picker dates. */
export function daysInclusive(start: Date, end: Date): number {
  const a = Date.UTC(start.getFullYear(), start.getMonth(), start.getDate());
  const b = Date.UTC(end.getFullYear(), end.getMonth(), end.getDate());
  return Math.round((b - a) / 86_400_000) + 1;
}

/** Why positions cannot be chosen for this range (null = they can). */
export function positionsBlockedReason(days: number, limits: ExportLimits): string | null {
  return days > limits.positions_max_days
    ? `At most ${limits.positions_max_days} days at a time. Choose ${limits.positions_max_days} days or fewer to include positions.`
    : null;
}

/** The request body of the form. */
export function requestBody(start: Date, end: Date, selected: ReadonlySet<DatasetKey>) {
  return {
    from_date: toApiDateString(start),
    to_date: toApiDateString(end),
    scope: DATASET_KEYS.filter((k) => selected.has(k)),
  };
}

export const isActive = (job: Pick<ExportJob, 'state'>): boolean => job.state === 'queued' || job.state === 'running';

const MONTHS = ['Jan', 'Feb', 'Mar', 'Apr', 'May', 'Jun', 'Jul', 'Aug', 'Sep', 'Oct', 'Nov', 'Dec'];
/** South Africa keeps UTC+2 all year (no daylight saving). */
const SAST_OFFSET_MS = 2 * 3_600_000;
const pad2 = (n: number): string => String(n).padStart(2, '0');

/** 28 Sep 2026, 10:15 (SAST; fixed format, not the browser's locale data). */
export function fmtWhen(iso: string | null | undefined): string {
  if (!iso) return '—';
  const ms = Date.parse(iso);
  if (!Number.isFinite(ms)) return '—';
  const d = new Date(ms + SAST_OFFSET_MS);
  return `${d.getUTCDate()} ${MONTHS[d.getUTCMonth()]} ${d.getUTCFullYear()}, ${pad2(d.getUTCHours())}:${pad2(d.getUTCMinutes())}`;
}

/** 1 Jul – 28 Sep 2026 (calendar days as the API gives them). */
export function fmtRange(from: string, to: string): string {
  const parse = (s: string) => /^(\d{4})-(\d{2})-(\d{2})$/.exec(s);
  const f = parse(from);
  const t = parse(to);
  if (!f || !t) return `${from} – ${to}`;
  const day = (m: RegExpExecArray, withYear: boolean) => `${Number(m[3])} ${MONTHS[Number(m[2]) - 1]}${withYear ? ` ${m[1]}` : ''}`;
  if (from === to) return day(f, true);
  return `${day(f, f[1] !== t[1])} – ${day(t, true)}`;
}

export function fmtBytes(n: number | null | undefined): string {
  if (n === null || n === undefined || !Number.isFinite(n)) return '—';
  if (n < 1024) return `${n} B`;
  const units = ['KB', 'MB', 'GB'];
  let v = n / 1024;
  let i = 0;
  while (v >= 1024 && i < units.length - 1) {
    v /= 1024;
    i += 1;
  }
  return `${v < 10 ? v.toFixed(1) : Math.round(v)} ${units[i]}`;
}

export const fmtCount = (n: number): string => Math.trunc(n).toString().replace(/\B(?=(\d{3})+(?!\d))/g, ',');

/** "3 of 12 files · trips.csv · 12,345 rows so far". */
export function progressText(job: Pick<ExportJob, 'state' | 'progress'>): string {
  const p = job.progress || {};
  if (job.state === 'queued') return 'Waiting to start';
  if (job.state !== 'running') return '';
  if (p.phase === 'finishing') return 'Finishing the ZIP file';
  const parts: string[] = [];
  if (p.files_total) parts.push(`${p.files_done ?? 0} of ${p.files_total} files`);
  if (p.file) parts.push(p.file);
  if (p.rows) parts.push(`${fmtCount(p.rows)} rows so far`);
  return parts.join(' · ') || 'Starting';
}

/** 0..1 for the progress bar (files done; null = indeterminate). */
export function progressRatio(job: Pick<ExportJob, 'state' | 'progress'>): number | null {
  const p = job.progress || {};
  if (job.state !== 'running' || !p.files_total) return null;
  return Math.max(0, Math.min(1, (p.files_done ?? 0) / p.files_total));
}

export const STATE_META: Record<ExportState, { label: string; className: string }> = {
  queued: { label: 'Queued', className: 'border border-dashed border-slate-400 text-slate-600' },
  running: { label: 'Preparing', className: 'border border-primary-300 bg-primary-50 text-primary-800' },
  done: { label: 'Ready', className: 'border border-emerald-300 bg-emerald-50 text-emerald-800' },
  failed: { label: 'Failed', className: 'border border-red-300 bg-red-50 text-red-700' },
  expired: { label: 'Deleted', className: 'border border-slate-300 bg-slate-100 text-slate-600' },
};

/** "All 17 datasets" / "Trips, Events" / "5 datasets". */
export function scopeSummary(scope: readonly string[], catalogue: readonly DatasetInfo[]): string {
  const available = catalogue.filter((d) => d.available).length || DATASET_KEYS.length;
  if (scope.length >= available) return `All ${scope.length} datasets`;
  if (scope.length <= 2) return scope.map((k) => labelOf(k, catalogue)).join(', ');
  return `${scope.length} datasets`;
}

/** "6 days left" / "less than an hour left". */
export function timeLeft(iso: string | null | undefined, now: number = Date.now()): string {
  if (!iso) return '';
  const ms = Date.parse(iso) - now;
  if (!Number.isFinite(ms) || ms <= 0) return '';
  const hours = Math.floor(ms / 3_600_000);
  if (hours < 1) return 'less than an hour left';
  if (hours < 48) return `${hours} hour${hours === 1 ? '' : 's'} left`;
  return `${Math.floor(hours / 24)} days left`;
}
